import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
  type EndVisitPhase,
  attachEndVisitContext,
  endEndVisitFlow,
  enterEndVisitPhase,
  exitEndVisitPhase,
  noteEndVisitEntry,
  recordVisibilityChange,
} from "services/endVisitTiming";

/**
 * The end-visit screens are query-param states, not routes. `?end_visit=1` is the interim screen
 * and `?end_visit=2` is the end note screen, both on /patients/:patientId - the Accept button
 * flips the param (EndVisitStepper sets end_visit=2) and the back button flips it back.
 *
 * This is why useScreenTimeTracking cannot see either screen: its effect depends only on
 * location.pathname, so a search-param transition never re-runs it, and the declared
 * /patients/:patientId/interimScreen route it keys off is unreachable - nothing navigates there.
 */
const PHASE_BY_PARAM: Record<string, EndVisitPhase> = {
  "1": "interim_screen",
  "2": "end_note",
};

export function readEndVisitPhase(search: string): EndVisitPhase | null {
  try {
    const param = new URLSearchParams(search).get("end_visit");
    if (!param) return null;
    return PHASE_BY_PARAM[param] ?? null;
  } catch {
    return null;
  }
}

/** Deep links that land in the flow without passing the End Visit button. */
function readDeepLinkEntry(search: string): "past_visit_resume" | "review_later" | "open_encounters" | null {
  try {
    const params = new URLSearchParams(search);
    if (params.get("review_later")) return "review_later";
    if (params.get("resume_visit")) return "past_visit_resume";
    // Open Encounters links carry end_visit=1 and an encounter_recording_id but no resume marker.
    // Indistinguishable from a fresh flow by URL alone, so it is only claimed when no flow is
    // already in progress - noteEndVisitEntry enforces that.
    return "open_encounters";
  } catch {
    return null;
  }
}

/**
 * Measures dwell time on the two end-visit screens. Mounted once in Router next to
 * useScreenTimeTracking, rather than inside PatientDetail: a single instance cannot double-count,
 * and it still fires when the provider navigates clean out of the flow.
 */
export function useEndVisitTiming(): void {
  const location = useLocation();
  const search = location.search;
  const isPatientDetail = /^\/patients\/[^/]+$/.test(location.pathname);
  const phase = isPatientDetail ? readEndVisitPhase(search) : null;
  const phaseRef = useRef<EndVisitPhase | null>(null);

  useEffect(() => {
    phaseRef.current = phase;

    if (!phase) {
      // Left the flow entirely. Close the open screen measurement; the flow itself stays open,
      // because a provider who wanders off to another patient and comes back has not abandoned it
      // - only an unload or an explicit terminal action ends the flow.
      exitEndVisitPhase();
      return;
    }

    const params = new URLSearchParams(search);
    const ctx = {
      encounterRecordingId: params.get("encounter_recording_id") || undefined,
      patientId: location.pathname.split("/")[2] || undefined,
    };

    const deepLink = readDeepLinkEntry(search);
    if (deepLink) {
      noteEndVisitEntry(deepLink, ctx);
    }
    // The recording id only appears in the URL once the upload has minted it, which is after the
    // flow already started. Backfill it so the flow event can be joined to its phase events.
    attachEndVisitContext(ctx);

    enterEndVisitPhase(phase, ctx);
  }, [phase, search, location.pathname]);

  // Hidden-tab accounting. Wall clock on the end note screen can run for hours while nobody is
  // looking at it, so active_ms ships alongside duration_ms and this is what feeds it.
  useEffect(() => {
    const onVisibility = () => recordVisibilityChange(document.visibilityState === "hidden");
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  // A tab closed on the end note screen is the ordinary way a flow ends without signing. Without
  // this flush the measurement is simply never emitted, which would bias every percentile toward
  // the providers who finish. pagehide rather than unload: unload is ignored by modern browsers
  // when a page is eligible for the back/forward cache, and never fires on mobile Safari.
  useEffect(() => {
    const onPageHide = () => {
      if (!phaseRef.current) return;
      exitEndVisitPhase();
      endEndVisitFlow("abandoned");
    };
    window.addEventListener("pagehide", onPageHide);
    return () => window.removeEventListener("pagehide", onPageHide);
  }, []);
}
